import type { CSSProperties, ElementType, ReactNode } from 'react';
import { typography, palette } from '../../styles/tokens';

type TextVariant =
  | 'hero'
  | 'title'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'caption'
  | 'micro';

type TextColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'inherit';

interface TextProps {
  variant?: TextVariant;
  color?: TextColor;
  as?: ElementType;
  weight?: 400 | 500 | 600 | 700 | 800;
  align?: 'left' | 'center' | 'right';
  truncate?: boolean;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}

const COLOR_MAP: Record<TextColor, string> = {
  primary: palette.g900,
  secondary: palette.g600,
  tertiary: palette.g500,
  accent: palette.accent,
  success: palette.green,
  warning: palette.amber,
  danger: palette.red,
  inherit: 'inherit',
};

const DEFAULT_TAG: Record<TextVariant, ElementType> = {
  hero: 'h1',
  title: 'h1',
  heading: 'h2',
  subheading: 'h3',
  body: 'p',
  caption: 'span',
  micro: 'span',
};

const VARIANT_STYLE: Record<TextVariant, { size: number; weight: string; lineHeight: number }> = {
  hero:       { size: typography.size.hero,       weight: typography.weight.bold,     lineHeight: typography.lineHeight.tight },
  title:      { size: typography.size.title,      weight: typography.weight.bold,     lineHeight: typography.lineHeight.tight },
  heading:    { size: typography.size.heading,     weight: typography.weight.semibold, lineHeight: typography.lineHeight.normal },
  subheading: { size: typography.size.subheading,  weight: typography.weight.medium,   lineHeight: typography.lineHeight.normal },
  body:       { size: typography.size.body,        weight: typography.weight.regular,  lineHeight: typography.lineHeight.normal },
  caption:    { size: typography.size.caption,     weight: typography.weight.regular,  lineHeight: typography.lineHeight.normal },
  micro:      { size: typography.size.micro,       weight: typography.weight.semibold, lineHeight: typography.lineHeight.normal },
};

export function Text({
  variant = 'body',
  color = 'primary',
  as,
  weight,
  align,
  truncate,
  style,
  className,
  children,
}: TextProps) {
  const Tag = as ?? DEFAULT_TAG[variant];
  const vs = VARIANT_STYLE[variant];

  const computedStyle: CSSProperties = {
    fontFamily: typography.fontFamily,
    fontSize: vs.size,
    fontWeight: weight ?? vs.weight,
    lineHeight: vs.lineHeight,
    color: COLOR_MAP[color],
    textAlign: align,
    margin: 0,
    ...(variant === 'micro' && {
      textTransform: 'uppercase' as const,
      letterSpacing: 0.3,
    }),
    ...(truncate && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    }),
    ...style,
  };

  return (
    <Tag style={computedStyle} className={className}>
      {children}
    </Tag>
  );
}
